import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { visit } from "unist-util-visit";
import type { Source } from "@/types/api";

interface CitationsContextValue {
  sourcesCount: number;
  sourceListId: string;
  highlight: (index: number) => void;
}

const CitationsContext = createContext<CitationsContextValue | null>(null);

export function useCitations(): CitationsContextValue | null {
  return useContext(CitationsContext);
}

interface CitationsProviderProps {
  sourceListId: string;
  sources: Source[];
  children: ReactNode;
}

export function CitationsProvider({ sourceListId, sources, children }: CitationsProviderProps) {
  const highlight = useCallback(
    (index: number) => {
      const target = document.getElementById(`${sourceListId}-${index}`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("ndc-source-highlight");
      window.setTimeout(() => target.classList.remove("ndc-source-highlight"), 1600);
    },
    [sourceListId],
  );

  const value = useMemo(
    () => ({ sourcesCount: sources.length, sourceListId, highlight }),
    [sources.length, sourceListId, highlight],
  );

  return (
    <CitationsContext.Provider value={value}>
      {children}
    </CitationsContext.Provider>
  );
}

/**
 * rehype plugin: walks text nodes, splits `[N]` patterns into <span class="ndc-citation" data-index="N">[N]</span>.
 * Only transforms indices that fall within the available source count.
 */
export function rehypeCitationChips({ maxIndex }: { maxIndex: number }) {
  return (tree: unknown) => {
    if (maxIndex <= 0) return;
    visit(tree as Parameters<typeof visit>[0], "text", (node: unknown, index, parent) => {
      const textNode = node as { type: string; value?: string };
      const parentNode = parent as { children?: unknown[]; tagName?: string } | null;
      if (!parentNode || index == null || typeof textNode.value !== "string") return;
      if (parentNode.tagName === "code" || parentNode.tagName === "pre") return;

      const value = textNode.value;
      const regex = /\[(\d+)\]/g;
      const segments: unknown[] = [];
      let lastIndex = 0;
      let matched = false;
      let match: RegExpExecArray | null;

      while ((match = regex.exec(value)) !== null) {
        const idx = parseInt(match[1], 10);
        if (!Number.isFinite(idx) || idx < 1 || idx > maxIndex) continue;

        matched = true;
        if (match.index > lastIndex) {
          segments.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }
        segments.push({
          type: "element",
          tagName: "span",
          properties: {
            className: ["ndc-citation"],
            "data-citation-index": String(idx),
          },
          children: [{ type: "text", value: `[${idx}]` }],
        });
        lastIndex = regex.lastIndex;
      }

      if (!matched) return;
      if (lastIndex < value.length) {
        segments.push({ type: "text", value: value.slice(lastIndex) });
      }

      parentNode.children!.splice(index, 1, ...segments);
      return ["skip", index + segments.length];
    });
  };
}
